import { useEffect, useState } from 'react';
import { Container, Grid } from '@material-ui/core';

import { useDialog, useNotification } from '#root/hooks';
import { userService } from '#root/services';
import {
  PaymentCheckoutDialog,
  ShippingAddressDrawer,
  ShippingDetailDrawer,
  ShippingPickupDrawer,
} from '#root/components';
import { PageHeader } from '#root/components/ui/composed/page-header/page-header';
import { GuveryEmpty } from '#root/ui-components/cards';
import ShippingDTO, { ShippingStatus } from '#root/models/Shipping';
import { ShippingDeliveryMan } from '#root/assets';
import ShipmentCard from './ShipmentCard';
import ShipmentCardSkeleton from './ShipmentCardSkeleton';
import ShipmentInfoPanel from './ShipmentInfoPanel';
import './userShipments.scss';

// Use any for complex API response types that may not be fully typed
type ShipmentData = any;

function UserShipments() {
  const { createMessage } = useNotification();
  const [loading, setLoading] = useState<boolean>(true);
  const [shipments, setShipments] = useState<ShipmentData[]>([]);
  const [selectedShipment, setSelectedShipment] = useState<ShippingDTO | null>(null);
  const [banks, setBanks] = useState<any[]>([]);
  const [currencies, setCurrencies] = useState<any[]>([]);
  const [paymentDialogSource, setPaymentDialogSource] = useState<any>({
    title: 'Pagar Envío',
    id: null,
    amount: '',
    currency: '',
    disabledCurrencies: true,
    disabledPrice: false,
    allowConvert: false,
    allowCredits: false,
    allowCoupon: false,
    amountHelper: 'Importe del envio:',
    showCredits: false,
    stepsArray: [
      'Selecciona el método y el banco.',
      'Escoge la moneda y el monto.',
      'Realiza el pago y confírmalo.',
    ],
    methodsHelper:
      'Usamos estos métodos para evitar el pago de comisión al banco, pues nuestras cuentas son de Lima.',
    paymentType: 'SHIPPING',
  });
  const addressDrawer = useDialog();
  const pickupDrawer = useDialog();
  const paymentDialog = useDialog();
  const detailDialog = useDialog();

  const handleOpenAddressDrawer = (record: ShipmentData) => {
    setSelectedShipment(record);
    addressDrawer.openDialog();
  };

  const handleOpenPickupDrawer = (record: ShipmentData) => {
    setSelectedShipment(record);
    pickupDrawer.openDialog();
  };

  const handleOpenPaymentDialog = (record: ShipmentData) => {
    setPaymentDialogSource({
      ...paymentDialogSource,
      id: record.id,
      amount: record.amount,
      currency: record.currency,
      tca: record.tca,
    });
    paymentDialog.openDialog();
  };

  const handleOpenDetailDialog = (record: ShipmentData) => {
    setSelectedShipment(record);
    detailDialog.openDialog();
  };

  const closePaymentDialog = () => {
    fetchUserShipments();
    paymentDialog.closeDialog();
  };

  const handleDrawerSuccess = () => {
    fetchUserShipments();
  };

  const fetchUserShipments = async () => {
    setLoading(true);
    const res = await userService.shipments();
    if (!res.error) {
      setShipments(res.data.shippings);
      setBanks(res.data.payment_checkout_dialog.banks);
      setCurrencies(res.data.payment_checkout_dialog.currencies);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchUserShipments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Calcular envíos que requieren atención
  const getSubtitle = () => {
    if (loading || shipments.length === 0) return undefined;

    const total = shipments.length;
    const totalText = `${total} envío${total !== 1 ? 's' : ''}`;

    const needsAddressCount = shipments.filter(
      (s) => s.current_status === ShippingStatus.registered
    ).length;

    const needsPaymentCount = shipments.filter(
      (s) =>
        (s.current_status === ShippingStatus.ready ||
          s.current_status === ShippingStatus.shipped) &&
        !s.paid_at
    ).length;

    const urgentCount = needsAddressCount + needsPaymentCount;

    if (urgentCount === 0) {
      return totalText;
    }

    return (
      <>
        {totalText}
        <span className="subtitleAlert">
          {` · ${urgentCount} requiere${urgentCount !== 1 ? 'n' : ''} atención`}
        </span>
      </>
    );
  };

  return (
    <div className="user-shipments guv-main-container">
      <Container maxWidth="lg">
        <PageHeader title="Mis Envíos Nacionales" subtitle={getSubtitle()} />
        {!loading && <ShipmentInfoPanel />}
        {!loading ? (
          <Grid container spacing={3}>
            {shipments.map((shipment, index) => (
              <Grid key={index} item xs={12}>
                <ShipmentCard
                  shipment={shipment}
                  handleOpenPaymentDialog={handleOpenPaymentDialog}
                  handleOpenAddressDrawer={handleOpenAddressDrawer}
                  handleOpenPickupDrawer={handleOpenPickupDrawer}
                  handleOpenDetailDialog={handleOpenDetailDialog}
                />
              </Grid>
            ))}
            {shipments.length === 0 && (
              <Grid item xs={12}>
                <GuveryEmpty description="No hay envíos" image={ShippingDeliveryMan} />
              </Grid>
            )}
          </Grid>
        ) : (
          <ShipmentCardSkeleton count={2} />
        )}
      </Container>

      {/* Drawer para confirmar envío a domicilio */}
      <ShippingAddressDrawer
        open={addressDrawer.open}
        onClose={addressDrawer.closeDialog}
        shipping={selectedShipment}
        onSuccess={handleDrawerSuccess}
        createMessage={createMessage}
      />

      {/* Drawer para confirmar recojo en persona */}
      <ShippingPickupDrawer
        open={pickupDrawer.open}
        onClose={pickupDrawer.closeDialog}
        shipping={selectedShipment}
        onSuccess={handleDrawerSuccess}
        createMessage={createMessage}
      />

      {/* Dialog de pago */}
      <PaymentCheckoutDialog
        open={paymentDialog.open}
        onClose={closePaymentDialog}
        dataSource={paymentDialogSource}
        banks={banks as any}
        currencies={currencies as any}
      />

      {/* Drawer de detalle */}
      <ShippingDetailDrawer
        open={detailDialog.open}
        onClose={detailDialog.closeDialog}
        shipping={selectedShipment}
        onOpenPayment={handleOpenPaymentDialog}
      />
    </div>
  );
}

export default UserShipments;
